import React, { useEffect, useState, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { collection, getDocs, getDoc, doc, updateDoc, deleteDoc, addDoc, query, where, writeBatch } from "firebase/firestore";
import AuthGuard from "../components/AuthGuard";
import Header from "../components/Header";
import Footer from "../components/Footer";
import EntityTable from "./EntityTable";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from 'react-toastify';

// Lazy load the profile and dashboard components
const ParentProfile = lazy(() => import('./ParentProfile'));
const ProfessionalProfile = lazy(() => import('./ProfessionalProfile'));
const OrganizationProfile = lazy(() => import('./OrganizationProfile'));
const ParentDashboard = lazy(() => import('./ParentsHome'));
const ProfessionalDashboard = lazy(() => import('./ProfessionalDashboard'));
const OrganizationDashboard = lazy(() => import('./OrganizationDashboard'));

const ENTITY_OPTIONS = [
  { key: "users", label: "Users", showDownload: true },
  { key: "parents", label: "Parents" },
  { key: "professionals", label: "Professionals" },
  { key: "organizations", label: "Organizations" },
  { key: "jobs", label: "Jobs" },
  { key: "assessments", label: "Assessments" },
];

const DATE_FILTERS = [
  { key: "day", label: "Last 1 Day" },
  { key: "week", label: "Last 1 Week" },
  { key: "month", label: "Last 1 Month" },
  { key: "all", label: "Till Date" },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(auth.currentUser);
  const [selectedEntity, setSelectedEntity] = useState("parents");
  const [dateFilter, setDateFilter] = useState("day");
  const [search, setSearch] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ isOpen: false, type: null, userId: null });
  
  // Placeholder states for each entity
  const [parents, setParents] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [error, setError] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const openModal = (type) => {
    setModal({ isOpen: true, type });
  };
  
  const closeModal = () => {
    setModal({ isOpen: false, type: null });
  };

  const exportUsersToCSV = async () => {
    if (isExporting) return;
    
    setIsExporting(true);
    try {
      // Get all users
      const usersCollection = collection(db, "users");
      const querySnapshot = await getDocs(usersCollection);
      
      // Convert to CSV
      const csvRows = [];
      // Add CSV header
      csvRows.push('"Display Name","Email","User ID","Created At"');
      
      // Add user data
      querySnapshot.forEach((doc) => {
        const user = doc.data();
        const row = [
          `"${user.displayName || ''}"`,
          `"${user.email || ''}"`,
          `"${doc.id}"`,
          `"${user.createdAt?.toDate?.() || ''}"`
        ].join(',');
        csvRows.push(row);
      });

      // Create CSV content
      const csvContent = csvRows.join('\n');
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error("Error exporting users to CSV:", error);
      setError('Failed to export users. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // --- Refresh Professionals Admin Action ---
  const [refreshingProfessionals, setRefreshingProfessionals] = useState(false);

  const refreshProfessionals = async () => {
  console.log('Refresh Professionals button clicked');
  setRefreshingProfessionals(true);
  try {
    // 1. Fetch all users where type === 'professional'
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('role', '==', 'professional'));
    const userSnaps = await getDocs(q);
    if (userSnaps.empty) {
      toast.info('No professional users found.');
      setRefreshingProfessionals(false);
      return;
    }
    // 2. For each, check if exists in professionals by UID
    const batch = writeBatch(db);
    let added = 0;
    for (const userDoc of userSnaps.docs) {
      const uid = userDoc.id;
      const userData = userDoc.data();
      const profRef = doc(db, 'professionals', uid);
      const profSnap = await getDoc(profRef);
      if (!profSnap.exists()) {
        batch.set(profRef, {
          fullName: userData.displayName || '',
          expertise: 'Therapist',
          email: userData.email || '',
          photoURL: userData.photoURL || '',
          createdAt: new Date(),
        });
        added++;
      }
    }
    if (added > 0) {
      await batch.commit();
      toast.success(`${added} professional profiles created.`);
    } else {
      toast.info('All professionals already exist.');
    }
  } catch (err) {
    console.error('Error refreshing professionals:', err);
    toast.error('Failed to refresh professionals.');
  } finally {
    setRefreshingProfessionals(false);
  }
};

const ADMIN_LINKS = [
  { 
    label: "Parent Profile", 
    onClick: () => openModal('parentProfile')
  },
  { 
    label: "Professional Profile", 
    onClick: () => openModal('professionalProfile')
  },
  { 
    label: "CDC Profile", 
    onClick: () => openModal('organizationProfile')
  },
  { 
    label: "Parent Dashboard", 
    onClick: () => openModal('parentDashboard')
  },
  { 
    label: "Professional Dashboard", 
    onClick: () => openModal('professionalDashboard')
  },
  { 
    label: "CDC Dashboard", 
    onClick: () => openModal('organizationDashboard')
  },
  { 
    label: refreshingProfessionals ? "Refreshing..." : "Refresh Professionals",
    onClick: refreshProfessionals,
    disabled: refreshingProfessionals,
  },
  { 
    label: "Admin Dashboard",
    onClick: () => navigate('/admin/dashboard')
  },
];

  // Add openDashboardModal to window object for EntityTable to use
  useEffect(() => {
    window.openDashboardModal = (modalType, userId) => {
      console.log('openDashboardModal called', { modalType, userId });
      setModal({ isOpen: true, type: modalType, userId });
      // Return true to indicate the function was called successfully
      return true;
    };

    // Cleanup function to remove the method when component unmounts
    return () => {
      delete window.openDashboardModal;
    };
  }, []);
  
  // Log modal state changes
  useEffect(() => {
    console.log('Modal state changed:', modal);
  }, [modal]);

  // Check if user is admin on component mount
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        // Check if user is the hardcoded admin or has admin role in Firestore
        if (currentUser.email === "asdfloteam@gmail.com") {
          setIsAdmin(true);
          setLoading(false);
          return;
        }

        // Check Firestore for admin role
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (userDoc.exists() && (userDoc.data().role === 'admin' || userDoc.data().isAdmin)) {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      } catch (err) {
        console.error("Error checking admin status:", err);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkAdminStatus();
  }, []);

  // Data fetching effect
  useEffect(() => {
    if (!isAdmin || loading) return;
    
    const fetchData = async () => {
      // Fetch data based on selected entity
      // TODO: Implement data fetching logic here
    };
    
    fetchData();
  }, [isAdmin, loading, selectedEntity, dateFilter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-red-500 text-5xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-6">You don't have permission to access the admin dashboard.</p>
          <button 
            onClick={() => window.history.back()} 
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <AuthGuard>
      <div className="admin-banner bg-blue-900 text-white text-center py-1 text-sm">
        Admin Mode
      </div>
      <Header adminMode={true} />
      <div className="min-h-[80vh] flex bg-gray-50">
        {/* Sidebar */}
        <aside className="w-56 bg-white border-r shadow-sm p-4 flex flex-col">
          <h2 className="text-lg font-bold mb-6">Entities</h2>
          <nav className="flex flex-col gap-2 mb-8">
            {ENTITY_OPTIONS.map(opt => (
              <div key={opt.key} className="flex items-center group relative">
                <button
                  className={`flex-1 text-left px-3 py-2 rounded transition font-medium ${
                    selectedEntity === opt.key ? "bg-blue-600 text-white" : "hover:bg-blue-100"
                  }`}
                  onClick={() => setSelectedEntity(opt.key)}
                >
                  {opt.label}
                </button>
                {opt.showDownload && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      exportUsersToCSV();
                    }}
                    disabled={isExporting}
                    className={`ml-2 p-1 rounded-full transition-colors ${
                      isExporting 
                        ? 'text-gray-400 cursor-not-allowed' 
                        : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                    title="Download as CSV"
                  >
                    {isExporting ? (
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className="h-5 w-5" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" 
                        />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            ))}
          </nav>
          <h2 className="text-lg font-bold mb-3 mt-4">Shortcuts</h2>
          <nav className="flex flex-col gap-2">
            {ADMIN_LINKS.map((link, index) => (
              <button
                key={index}
                className={`text-left px-3 py-2 rounded transition font-medium hover:bg-green-100 ${link.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={link.onClick}
                disabled={!!link.disabled}
              >
                {link.label}
                {link.disabled && link.label.toLowerCase().includes('refresh') && (
                  <span className="ml-2 animate-spin inline-block">⏳</span>
                )}
              </button>
            ))}
          </nav>
        </aside>
        
        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
            <div className="flex gap-2 items-center">
              <select
                className="border rounded px-2 py-1"
                value={dateFilter}
                onChange={e => setDateFilter(e.target.value)}
              >
                {DATE_FILTERS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
              <input
                type="text"
                placeholder="Search..."
                className="border rounded px-2 py-1"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
          
          {error && <div className="text-red-500 mb-2">{error}</div>}
          
          {/* Entity Management Tables */}
          <section className="bg-white rounded shadow p-4">
            <h2 className="text-xl font-semibold mb-4">
              {ENTITY_OPTIONS.find(e => e.key === selectedEntity)?.label} Management
            </h2>
            {selectedEntity === "users" && (
              <EntityTable
                entityKey="users"
                collectionName="users"
                search={search}
                dateFilter={dateFilter}
              />
            )}
            {selectedEntity === "parents" && (
              <EntityTable
                entityKey="parents"
                collectionName="users"
                search={search}
                dateFilter={dateFilter}
              />
            )}
            {selectedEntity === "professionals" && (
              <EntityTable
                entityKey="professionals"
                collectionName="users"
                search={search}
                dateFilter={dateFilter}
              />
            )}
            {selectedEntity === "organizations" && (
              <EntityTable
                entityKey="organizations"
                collectionName="organizations"
                search={search}
                dateFilter={dateFilter}
              />
            )}
            {selectedEntity === "jobs" && (
              <EntityTable
                entityKey="jobs"
                collectionName="jobs"
                search={search}
                dateFilter={dateFilter}
              />
            )}
            {selectedEntity === "assessments" && (
              <EntityTable
                entityKey="assessments"
                collectionName="assessments"
                search={search}
                dateFilter={dateFilter}
              />
            )}
          </section>
        </main>
      </div>
      
      {/* Modal for displaying profiles and dashboards */}
      {modal.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
              onClick={closeModal}
              aria-hidden="true"
            ></div>

            {/* Modal panel */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="w-full">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {modal.type?.includes('Dashboard') ? 'Dashboard' : 'Profile'} View
                      </h3>
                      <button
                        type="button"
                        className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                        onClick={closeModal}
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    <div className="mt-2">
                      <Suspense fallback={<div className="flex justify-center p-8">Loading...</div>}>
                        {modal.type === 'parentProfile' && <ParentProfile adminView />}
                        {modal.type === 'professionalProfile' && <ProfessionalProfile adminView />}
                        {modal.type === 'organizationProfile' && <OrganizationProfile adminView />}
                        {modal.type === 'parentDashboard' && (
                          <ParentDashboard 
                            adminView={true}
                            userId={modal.userId || user?.uid}
                          />
                        )}
                        {modal.type === 'professionalDashboard' && (
                          <ProfessionalDashboard 
                            adminView={true} 
                            userId={modal.userId || user?.uid}
                          />
                        )}
                        {modal.type === 'organizationDashboard' && (
                          <OrganizationDashboard 
                            adminView={true}
                            userId={modal.userId || user?.uid}
                          />
                        )}
                      </Suspense>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </AuthGuard>
  );
};

export default AdminDashboard;
